import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';

import { PowerThresholdBar } from '../../../../shared/components/power-threshold-bar/power-threshold-bar';

/** The thresholds form the editor drives (built by the profile dialog). */
export type ThresholdForm = FormGroup<{
  lowPowerPercentage: FormControl<number>;
  lowPowerPercentageOut: FormControl<number>;
  noPowerPercentage: FormControl<number>;
  noPowerPercentageOut: FormControl<number>;
}>;

/** Editor-local names for the four thresholds (enter/exit per band). */
type Key = 'npEnter' | 'npExit' | 'lpEnter' | 'lpExit';

const CONTROL: Record<Key, keyof ThresholdForm['controls']> = {
  npEnter: 'noPowerPercentage',
  npExit: 'noPowerPercentageOut',
  lpEnter: 'lowPowerPercentage',
  lpExit: 'lowPowerPercentageOut',
};

interface Marker {
  key: Key;
  /** Enter markers sit above the track, exit markers below. */
  above: boolean;
  enter: boolean;
  tone: 'red' | 'orange';
}

export type ThresholdValues = Record<keyof ThresholdForm['controls'], number>;

export interface ThresholdPreset {
  key: 'recommended' | 'protect' | 'uptime';
  values: ThresholdValues;
}

/** Named starting points; the review step also uses these to label a match. */
export const THRESHOLD_PRESETS: readonly ThresholdPreset[] = [
  {
    key: 'recommended',
    values: {
      lowPowerPercentage: 20,
      lowPowerPercentageOut: 25,
      noPowerPercentage: 5,
      noPowerPercentageOut: 10,
    },
  },
  {
    key: 'protect',
    values: {
      lowPowerPercentage: 30,
      lowPowerPercentageOut: 40,
      noPowerPercentage: 10,
      noPowerPercentageOut: 15,
    },
  },
  {
    key: 'uptime',
    values: {
      lowPowerPercentage: 12,
      lowPowerPercentageOut: 16,
      noPowerPercentage: 3,
      noPowerPercentageOut: 6,
    },
  },
];

/** The preset exactly matching `values`, or null for a custom configuration. */
export function matchThresholdPreset(values: ThresholdValues): ThresholdPreset | null {
  return (
    THRESHOLD_PRESETS.find((preset) =>
      (Object.keys(preset.values) as (keyof ThresholdValues)[]).every(
        (k) => values[k] === preset.values[k],
      ),
    ) ?? null
  );
}

/**
 * Visual editor for the battery power thresholds (from the "Power Thresholds
 * Step" design): a segmented 0-100% track with four draggable markers, preset
 * buttons, and stepper value fields. Ordering (noPower enter < exit <=
 * lowPower enter < exit) is enforced by clamping on every interaction, so the
 * form can't be driven into a cross-field-invalid state from here.
 *
 * The custom stepper fields (not app-number-input) are part of the composite
 * widget: they clamp against sibling values and stay in lockstep with the
 * track, which a generic form control can't do.
 */
@Component({
  selector: 'app-threshold-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, PowerThresholdBar],
  templateUrl: './threshold-editor.html',
  styleUrl: './threshold-editor.css',
})
export class ThresholdEditor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  readonly form = input.required<ThresholdForm>();

  /** Tear-down for the drag in progress, if any. */
  private stopDrag: (() => void) | null = null;

  private readonly trackEl = viewChild<ElementRef<HTMLElement>>('track');

  /** Mirror of the form values (form state isn't signal-based). */
  private readonly values = signal<Record<Key, number>>({
    npEnter: 0,
    npExit: 0,
    lpEnter: 0,
    lpExit: 0,
  });

  protected readonly dragKey = signal<Key | null>(null);
  protected readonly focusedKey = signal<Key | null>(null);
  protected readonly editText = signal('');

  protected readonly markers: readonly Marker[] = [
    { key: 'npEnter', above: true, enter: true, tone: 'red' },
    { key: 'npExit', above: false, enter: false, tone: 'red' },
    { key: 'lpEnter', above: true, enter: true, tone: 'orange' },
    { key: 'lpExit', above: false, enter: false, tone: 'orange' },
  ];

  protected readonly presets = THRESHOLD_PRESETS;

  /** Cards below the track: low power first, each with enter + exit fields. */
  protected readonly cards = [
    { kicker: 'lowPower', tone: 'orange', fields: ['lpEnter', 'lpExit'] as Key[] },
    { kicker: 'noPower', tone: 'red', fields: ['npEnter', 'npExit'] as Key[] },
  ];

  constructor() {
    // Keep the local mirror in sync with the bound form (values arrive from
    // edit-mode patching and leave via this editor's own writes).
    effect((onCleanup) => {
      const form = this.form();
      this.readForm(form);
      const sub = form.valueChanges.subscribe(() => this.readForm(form));
      onCleanup(() => sub.unsubscribe());
    });

    this.destroyRef.onDestroy(() => this.stopDrag?.());
  }

  private readForm(form: ThresholdForm): void {
    const raw = form.getRawValue();
    this.values.set({
      npEnter: raw.noPowerPercentage ?? 0,
      npExit: raw.noPowerPercentageOut ?? 0,
      lpEnter: raw.lowPowerPercentage ?? 0,
      lpExit: raw.lowPowerPercentageOut ?? 0,
    });
  }

  // ── Value access ─────────────────────────────────────────────────────

  protected value(key: Key): number {
    return this.values()[key];
  }

  /** Field shows the in-progress edit text while focused, the value otherwise. */
  protected fieldText(key: Key): string {
    return this.focusedKey() === key ? this.editText() : String(this.value(key));
  }

  /** Valid range for a threshold given its neighbours (keeps ordering). */
  private bounds(key: Key): [number, number] {
    const v = this.values();
    switch (key) {
      case 'npEnter':
        return [0, v.npExit - 1];
      case 'npExit':
        return [v.npEnter + 1, v.lpEnter];
      case 'lpEnter':
        return [v.npExit, v.lpExit - 1];
      case 'lpExit':
        return [v.lpEnter + 1, 100];
    }
  }

  private setValue(key: Key, raw: number): void {
    if (Number.isNaN(raw)) return;
    const [min, max] = this.bounds(key);
    // Inverted bounds mean the loaded data violates the ordering contract;
    // refuse per-key edits (they would clamp to nonsense) — presets repair.
    if (min > max) return;
    const clamped = Math.min(max, Math.max(min, Math.round(raw)));
    const control = this.form().controls[CONTROL[key]];
    if (control.value === clamped) return;
    control.setValue(clamped);
    control.markAsDirty();
  }

  protected applyPreset(preset: ThresholdPreset): void {
    // Presets are valid-by-construction complete sets: apply atomically,
    // bypassing the per-key clamp (which exists for incremental gestures and
    // does not converge for wholesale replacement).
    const form = this.form();
    form.patchValue(preset.values);
    form.markAsDirty();
  }

  protected isPresetActive(preset: ThresholdPreset): boolean {
    const v = this.values();
    return (['npEnter', 'npExit', 'lpEnter', 'lpExit'] as Key[]).every(
      (k) => v[k] === preset.values[CONTROL[k]],
    );
  }

  // ── Track markers: drag + keyboard ───────────────────────────────────

  protected startDrag(key: Key, event: PointerEvent): void {
    event.preventDefault();
    this.stopDrag?.();
    const track = this.trackEl()?.nativeElement;
    if (!track) return;
    // The track can't move mid-drag (pointer capture, no scroll): measure once
    // instead of forcing a reflow per pointermove.
    const rect = track.getBoundingClientRect();
    this.dragKey.set(key);
    const move = (ev: PointerEvent) =>
      this.setValue(key, ((ev.clientX - rect.left) / rect.width) * 100);
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      this.stopDrag = null;
      this.dragKey.set(null);
    };
    // Outside the zone: pointermove at frame rate must not tick app-wide CD;
    // the signal/form writes in setValue schedule rendering on real changes.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    });
    this.stopDrag = stop;
  }

  protected nudge(key: Key, event: KeyboardEvent): void {
    const step = event.shiftKey ? 5 : 1;
    let delta = 0;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step;
    if (!delta) return;
    event.preventDefault();
    this.setValue(key, this.value(key) + delta);
  }

  // ── Stepper fields ───────────────────────────────────────────────────

  protected onFieldFocus(key: Key, input: HTMLInputElement): void {
    this.focusedKey.set(key);
    this.editText.set(String(this.value(key)));
    input.select();
  }

  protected onFieldInput(field: HTMLInputElement): void {
    const sanitized = field.value.replace(/[^0-9]/g, '').slice(0, 3);
    // Explicit DOM writeback: when sanitizing lands on the current signal
    // value, Angular skips the [value] write and the rejected char would
    // otherwise stay visible.
    if (field.value !== sanitized) field.value = sanitized;
    this.editText.set(sanitized);
  }

  protected commitField(key: Key): void {
    const parsed = parseInt(this.editText(), 10);
    if (!Number.isNaN(parsed)) this.setValue(key, parsed);
    this.focusedKey.set(null);
    this.editText.set('');
  }

  protected onFieldKeydown(key: Key, event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.stepField(key, event.key === 'ArrowUp' ? 1 : -1);
    }
  }

  protected stepField(key: Key, delta: number): void {
    const parsed = parseInt(this.editText(), 10);
    const current = this.focusedKey() === key && !Number.isNaN(parsed) ? parsed : this.value(key);
    this.setValue(key, current + delta);
    if (this.focusedKey() === key) this.editText.set(String(this.value(key)));
  }
}
